import { __decorate } from "tslib";
import { SerializationHelper, serialize, serializeAsTexture, expandToProperty, serializeAsColor3 } from "../../Misc/decorators";
import { Color3 } from '../../Maths/math.color';
import { MaterialFlags } from "../materialFlags";
import { MaterialHelper } from "../../Materials/materialHelper";
/**
 * Define the code related to the clear coat parameters of the pbr material.
 */
var PBRClearCoatConfiguration = /** @class */function () {
  /**
   * Instantiate a new istance of clear coat configuration.
   * @param markAllSubMeshesAsTexturesDirty Callback to flag the material to dirty
   */
  function PBRClearCoatConfiguration(markAllSubMeshesAsTexturesDirty) {
    this._isEnabled = false;
    /**
     * Defines if the clear coat is enabled in the material.
     */
    this.isEnabled = false;
    /**
     * Defines the clear coat layer strength (between 0 and 1) it defaults to 1.
     */
    this.intensity = 1;
    /**
     * Defines the clear coat layer roughness.
     */
    this.roughness = 0;
    this._indexOfRefraction = PBRClearCoatConfiguration._DefaultIndexOfRefraction;
    /**
     * Defines the index of refraction of the clear coat.
     * This defaults to 1.5 corresponding to a 0.04 f0 or a 4% reflectance at normal incidence
     * The default fits with a polyurethane material.
     * Changing the default value is more performance intensive.
     */
    this.indexOfRefraction = PBRClearCoatConfiguration._DefaultIndexOfRefraction;
    this._texture = null;
    /**
     * Stores the clear coat values in a texture (red channel is intensity and green channel is roughness)
     * If useRoughnessFromMainTexture is false, the green channel of texture is not used and the green channel of textureRoughness is used instead
     * if textureRoughness is not empty, else no texture roughness is used
     */
    this.texture = null;
    this._useRoughnessFromMainTexture = true;
    /**
     * Indicates that the green channel of the texture property will be used for roughness (default: true)
     * If false, the green channel from textureRoughness is used for roughness
     */
    this.useRoughnessFromMainTexture = true;
    this._textureRoughness = null;
    /**
     * Stores the clear coat roughness in a texture (green channel)
     * Not used if useRoughnessFromMainTexture is true
     */
    this.textureRoughness = null;
    this._remapF0OnInterfaceChange = true;
    /**
     * Defines if the F0 value should be remapped to account for the interface change in the material.
     */
    this.remapF0OnInterfaceChange = true;
    this._bumpTexture = null;
    /**
     * Define the clear coat specific bump texture.
     */
    this.bumpTexture = null;
    this._isTintEnabled = false;
    /**
     * Defines if the clear coat tint is enabled in the material.
     */
    this.isTintEnabled = false;
    /**
     * Defines the clear coat tint of the material.
     * This is only use if tint is enabled
     */
    this.tintColor = Color3.White();
    /**
     * Defines the distance at which the tint color should be found in the
     * clear coat media.
     * This is only use if tint is enabled
     */
    this.tintColorAtDistance = 1;
    /**
     * Defines the clear coat layer thickness.
     * This is only use if tint is enabled
     */
    this.tintThickness = 1;
    this._tintTexture = null;
    /**
     * Stores the clear tint values in a texture.
     * rgb is tint
     * a is a thickness factor
     */
    this.tintTexture = null;
    this._internalMarkAllSubMeshesAsTexturesDirty = markAllSubMeshesAsTexturesDirty;
  }
  /** @hidden */
  PBRClearCoatConfiguration.prototype._markAllSubMeshesAsTexturesDirty = function () {
    this._internalMarkAllSubMeshesAsTexturesDirty();
  };
  /**
   * Gets wehter the submesh is ready to be used or not.
   * @param defines the list of "defines" to update.
   * @param scene defines the scene the material belongs to.
   * @param engine defines the engine the material belongs to.
   * @param disableBumpMap defines wether the material disables bump or not.
   * @returns - boolean indicating that the submesh is ready or not.
   */
  PBRClearCoatConfiguration.prototype.isReadyForSubMesh = function (defines, scene, engine, disableBumpMap) {
    if (defines._areTexturesDirty) {
      if (scene.texturesEnabled) {
        if (this._texture && MaterialFlags.ClearCoatTextureEnabled) {
          if (!this._texture.isReadyOrNotBlocking()) {
            return false;
          }
        }
        if (this._textureRoughness && MaterialFlags.ClearCoatTextureEnabled) {
          if (!this._textureRoughness.isReadyOrNotBlocking()) {
            return false;
          }
        }
        if (engine.getCaps().standardDerivatives && this._bumpTexture && MaterialFlags.ClearCoatBumpTextureEnabled && !disableBumpMap) {
          // Bump texture cannot be not blocking.
          if (!this._bumpTexture.isReady()) {
            return false;
          }
        }
        if (this._isTintEnabled && this._tintTexture && MaterialFlags.ClearCoatTintTextureEnabled) {
          if (!this._tintTexture.isReadyOrNotBlocking()) {
            return false;
          }
        }
      }
    }
    return true;
  };
  /**
   * Checks to see if a texture is used in the material.
   * @param defines the list of "defines" to update.
   * @param scene defines the scene to the material belongs to.
   */
  PBRClearCoatConfiguration.prototype.prepareDefines = function (defines, scene) {
    var _a;
    if (this._isEnabled) {
      defines.CLEARCOAT = true;
      defines.CLEARCOAT_USE_ROUGHNESS_FROM_MAINTEXTURE = this._useRoughnessFromMainTexture;
      defines.CLEARCOAT_TEXTURE_ROUGHNESS_IDENTICAL = this._texture !== null && this._texture._texture === ((_a = this._textureRoughness) === null || _a === void 0 ? void 0 : _a._texture) && this._texture.checkTransformsAreIdentical(this._textureRoughness);
      defines.CLEARCOAT_REMAP_F0 = this._remapF0OnInterfaceChange;
      if (defines._areTexturesDirty) {
        if (scene.texturesEnabled) {
          if (this._texture && MaterialFlags.ClearCoatTextureEnabled) {
            MaterialHelper.PrepareDefinesForMergedUV(this._texture, defines, "CLEARCOAT_TEXTURE");
          } else {
            defines.CLEARCOAT_TEXTURE = false;
          }
          if (this._textureRoughness && MaterialFlags.ClearCoatTextureEnabled) {
            MaterialHelper.PrepareDefinesForMergedUV(this._textureRoughness, defines, "CLEARCOAT_TEXTURE_ROUGHNESS");
          } else {
            defines.CLEARCOAT_TEXTURE_ROUGHNESS = false;
          }
          if (this._bumpTexture && MaterialFlags.ClearCoatBumpTextureEnabled) {
            MaterialHelper.PrepareDefinesForMergedUV(this._bumpTexture, defines, "CLEARCOAT_BUMP");
          } else {
            defines.CLEARCOAT_BUMP = false;
          }
          defines.CLEARCOAT_DEFAULTIOR = this._indexOfRefraction === PBRClearCoatConfiguration._DefaultIndexOfRefraction;
          if (this._isTintEnabled) {
            defines.CLEARCOAT_TINT = true;
            if (this._tintTexture && MaterialFlags.ClearCoatTintTextureEnabled) {
              MaterialHelper.PrepareDefinesForMergedUV(this._tintTexture, defines, "CLEARCOAT_TINT_TEXTURE");
            } else {
              defines.CLEARCOAT_TINT_TEXTURE = false;
            }
          } else {
            defines.CLEARCOAT_TINT = false;
            defines.CLEARCOAT_TINT_TEXTURE = false;
          }
        }
      }
    } else {
      defines.CLEARCOAT = false;
      defines.CLEARCOAT_TEXTURE = false;
      defines.CLEARCOAT_TEXTURE_ROUGHNESS = false;
      defines.CLEARCOAT_BUMP = false;
      defines.CLEARCOAT_TINT = false;
      defines.CLEARCOAT_TINT_TEXTURE = false;
      defines.CLEARCOAT_USE_ROUGHNESS_FROM_MAINTEXTURE = false;
      defines.CLEARCOAT_TEXTURE_ROUGHNESS_IDENTICAL = false;
    }
  };
  /**
   * Binds the material data.
   * @param uniformBuffer defines the Uniform buffer to fill in.
   * @param scene defines the scene the material belongs to.
   * @param engine defines the engine the material belongs to.
   * @param disableBumpMap defines wether the material disables bump or not.
   * @param isFrozen defines wether the material is frozen or not.
   * @param invertNormalMapX If sets to true, x component of normal map value will be inverted (x = 1.0 - x).
   * @param invertNormalMapY If sets to true, y component of normal map value will be inverted (y = 1.0 - y).
   * @param subMesh the submesh to bind data for
   */
  PBRClearCoatConfiguration.prototype.bindForSubMesh = function (uniformBuffer, scene, engine, disableBumpMap, isFrozen, invertNormalMapX, invertNormalMapY, subMesh) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var defines = subMesh._materialDefines;
    var identicalTextures = defines.CLEARCOAT_TEXTURE_ROUGHNESS_IDENTICAL;
    if (!uniformBuffer.useUbo || !isFrozen || !uniformBuffer.isSync) {
      if (identicalTextures && MaterialFlags.ClearCoatTextureEnabled) {
        uniformBuffer.updateFloat4("vClearCoatInfos", this._texture.coordinatesIndex, this._texture.level, -1, -1);
        MaterialHelper.BindTextureMatrix(this._texture, uniformBuffer, "clearCoat");
      } else if ((this._texture || this._textureRoughness) && MaterialFlags.ClearCoatTextureEnabled) {
        uniformBuffer.updateFloat4("vClearCoatInfos", (_b = (_a = this._texture) === null || _a === void 0 ? void 0 : _a.coordinatesIndex) !== null && _b !== void 0 ? _b : 0, (_d = (_c = this._texture) === null || _c === void 0 ? void 0 : _c.level) !== null && _d !== void 0 ? _d : 0, (_f = (_e = this._textureRoughness) === null || _e === void 0 ? void 0 : _e.coordinatesIndex) !== null && _f !== void 0 ? _f : 0, (_h = (_g = this._textureRoughness) === null || _g === void 0 ? void 0 : _g.level) !== null && _h !== void 0 ? _h : 0);
        if (this._texture) {
          MaterialHelper.BindTextureMatrix(this._texture, uniformBuffer, "clearCoat");
        }
        if (this._textureRoughness && !identicalTextures && !defines.CLEARCOAT_USE_ROUGHNESS_FROM_MAINTEXTURE) {
          MaterialHelper.BindTextureMatrix(this._textureRoughness, uniformBuffer, "clearCoatRoughness");
        }
      }
      if (this._bumpTexture && engine.getCaps().standardDerivatives && MaterialFlags.ClearCoatTextureEnabled && !disableBumpMap) {
        uniformBuffer.updateFloat2("vClearCoatBumpInfos", this._bumpTexture.coordinatesIndex, this._bumpTexture.level);
        MaterialHelper.BindTextureMatrix(this._bumpTexture, uniformBuffer, "clearCoatBump");
        if (scene._mirroredCameraPosition) {
          uniformBuffer.updateFloat2("vClearCoatTangentSpaceParams", invertNormalMapX ? 1.0 : -1.0, invertNormalMapY ? 1.0 : -1.0);
        } else {
          uniformBuffer.updateFloat2("vClearCoatTangentSpaceParams", invertNormalMapX ? -1.0 : 1.0, invertNormalMapY ? -1.0 : 1.0);
        }
      }
      if (this._tintTexture && MaterialFlags.ClearCoatTintTextureEnabled) {
        uniformBuffer.updateFloat2("vClearCoatTintInfos", this._tintTexture.coordinatesIndex, this._tintTexture.level);
        MaterialHelper.BindTextureMatrix(this._tintTexture, uniformBuffer, "clearCoatTint");
      }
      // Clear Coat General params
      uniformBuffer.updateFloat2("vClearCoatParams", this.intensity, this.roughness);
      // Clear Coat Refraction params
      var a = 1 - this._indexOfRefraction;
      var b = 1 + this._indexOfRefraction;
      var f0 = Math.pow(-a / b, 2); // Schlicks approx: (ior1 - ior2) / (ior1 + ior2) where ior2 for air is close to vacuum = 1.
      var eta = 1 / this._indexOfRefraction;
      uniformBuffer.updateFloat4("vClearCoatRefractionParams", f0, eta, a, b);
      if (this._isTintEnabled) {
        uniformBuffer.updateFloat4("vClearCoatTintParams", this.tintColor.r, this.tintColor.g, this.tintColor.b, Math.max(0.00001, this.tintThickness));
        uniformBuffer.updateFloat("clearCoatColorAtDistance", Math.max(0.00001, this.tintColorAtDistance));
      }
    }
    // Textures
    if (scene.texturesEnabled) {
      if (this._texture && MaterialFlags.ClearCoatTextureEnabled) {
        uniformBuffer.setTexture("clearCoatSampler", this._texture);
      }
      if (this._textureRoughness && !identicalTextures && !defines.CLEARCOAT_USE_ROUGHNESS_FROM_MAINTEXTURE && MaterialFlags.ClearCoatTextureEnabled) {
        uniformBuffer.setTexture("clearCoatRoughnessSampler", this._textureRoughness);
      }
      if (this._bumpTexture && engine.getCaps().standardDerivatives && MaterialFlags.ClearCoatBumpTextureEnabled && !disableBumpMap) {
        uniformBuffer.setTexture("clearCoatBumpSampler", this._bumpTexture);
      }
      if (this._isTintEnabled && this._tintTexture && MaterialFlags.ClearCoatTintTextureEnabled) {
        uniformBuffer.setTexture("clearCoatTintSampler", this._tintTexture);
      }
    }
  };
  /**
   * Checks to see if a texture is used in the material.
   * @param texture - Base texture to use.
   * @returns - Boolean specifying if a texture is used in the material.
   */
  PBRClearCoatConfiguration.prototype.hasTexture = function (texture) {
    if (this._texture === texture) {
      return true;
    }
    if (this._textureRoughness === texture) {
      return true;
    }
    if (this._bumpTexture === texture) {
      return true;
    }
    if (this._tintTexture === texture) {
      return true;
    }
    return false;
  };
  /**
   * Returns an array of the actively used textures.
   * @param activeTextures Array of BaseTextures
   */
  PBRClearCoatConfiguration.prototype.getActiveTextures = function (activeTextures) {
    if (this._texture) {
      activeTextures.push(this._texture);
    }
    if (this._textureRoughness) {
      activeTextures.push(this._textureRoughness);
    }
    if (this._bumpTexture) {
      activeTextures.push(this._bumpTexture);
    }
    if (this._tintTexture) {
      activeTextures.push(this._tintTexture);
    }
  };
  /**
   * Returns the animatable textures.
   * @param animatables Array of animatable textures.
   */
  PBRClearCoatConfiguration.prototype.getAnimatables = function (animatables) {
    if (this._texture && this._texture.animations && this._texture.animations.length > 0) {
      animatables.push(this._texture);
    }
    if (this._textureRoughness && this._textureRoughness.animations && this._textureRoughness.animations.length > 0) {
      animatables.push(this._textureRoughness);
    }
    if (this._bumpTexture && this._bumpTexture.animations && this._bumpTexture.animations.length > 0) {
      animatables.push(this._bumpTexture);
    }
    if (this._tintTexture && this._tintTexture.animations && this._tintTexture.animations.length > 0) {
      animatables.push(this._tintTexture);
    }
  };
  /**
   * Disposes the resources of the material.
   * @param forceDisposeTextures - Forces the disposal of all textures.
   */
  PBRClearCoatConfiguration.prototype.dispose = function (forceDisposeTextures) {
    var _a, _b, _c, _d;
    if (forceDisposeTextures) {
      (_a = this._texture) === null || _a === void 0 ? void 0 : _a.dispose();
      (_b = this._textureRoughness) === null || _b === void 0 ? void 0 : _b.dispose();
      (_c = this._bumpTexture) === null || _c === void 0 ? void 0 : _c.dispose();
      (_d = this._tintTexture) === null || _d === void 0 ? void 0 : _d.dispose();
    }
  };
  /**
  * Get the current class name of the texture useful for serialization or dynamic coding.
  * @returns "PBRClearCoatConfiguration"
  */
  PBRClearCoatConfiguration.prototype.getClassName = function () {
    return "PBRClearCoatConfiguration";
  };
  /**
   * Add fallbacks to the effect fallbacks list.
   * @param defines defines the Base texture to use.
   * @param fallbacks defines the current fallback list.
   * @param currentRank defines the current fallback rank.
   * @returns the new fallback rank.
   */
  PBRClearCoatConfiguration.AddFallbacks = function (defines, fallbacks, currentRank) {
    if (defines.CLEARCOAT_BUMP) {
      fallbacks.addFallback(currentRank++, "CLEARCOAT_BUMP");
    }
    if (defines.CLEARCOAT_TINT) {
      fallbacks.addFallback(currentRank++, "CLEARCOAT_TINT");
    }
    if (defines.CLEARCOAT) {
      fallbacks.addFallback(currentRank++, "CLEARCOAT");
    }
    return currentRank;
  };
  /**
   * Add the required uniforms to the current list.
   * @param uniforms defines the current uniform list.
   */
  PBRClearCoatConfiguration.AddUniforms = function (uniforms) {
    uniforms.push("vClearCoatTangentSpaceParams", "vClearCoatParams", "vClearCoatRefractionParams", "vClearCoatTintParams", "clearCoatColorAtDistance", "clearCoatMatrix", "clearCoatRoughnessMatrix", "clearCoatBumpMatrix", "clearCoatTintMatrix", "vClearCoatInfos", "vClearCoatBumpInfos", "vClearCoatTintInfos");
  };
  /**
   * Add the required samplers to the current list.
   * @param samplers defines the current sampler list.
   */
  PBRClearCoatConfiguration.AddSamplers = function (samplers) {
    samplers.push("clearCoatSampler", "clearCoatRoughnessSampler", "clearCoatBumpSampler", "clearCoatTintSampler");
  };
  /**
   * Add the required uniforms to the current buffer.
   * @param uniformBuffer defines the current uniform buffer.
   */
  PBRClearCoatConfiguration.PrepareUniformBuffer = function (uniformBuffer) {
    uniformBuffer.addUniform("vClearCoatParams", 2);
    uniformBuffer.addUniform("vClearCoatRefractionParams", 4);
    uniformBuffer.addUniform("vClearCoatInfos", 4);
    uniformBuffer.addUniform("clearCoatMatrix", 16);
    uniformBuffer.addUniform("clearCoatRoughnessMatrix", 16);
    uniformBuffer.addUniform("vClearCoatBumpInfos", 2);
    uniformBuffer.addUniform("vClearCoatTangentSpaceParams", 2);
    uniformBuffer.addUniform("clearCoatBumpMatrix", 16);
    uniformBuffer.addUniform("vClearCoatTintParams", 4);
    uniformBuffer.addUniform("clearCoatColorAtDistance", 1);
    uniformBuffer.addUniform("vClearCoatTintInfos", 2);
    uniformBuffer.addUniform("clearCoatTintMatrix", 16);
  };
  /**
   * Makes a duplicate of the current configuration into another one.
   * @param clearCoatConfiguration define the config where to copy the info
   */
  PBRClearCoatConfiguration.prototype.copyTo = function (clearCoatConfiguration) {
    SerializationHelper.Clone(function () {
      return clearCoatConfiguration;
    }, this);
  };
  /**
   * Serializes this clear coat configuration.
   * @returns - An object with the serialized config.
   */
  PBRClearCoatConfiguration.prototype.serialize = function () {
    return SerializationHelper.Serialize(this);
  };
  /**
   * Parses a anisotropy Configuration from a serialized object.
   * @param source - Serialized object.
   * @param scene Defines the scene we are parsing for
   * @param rootUrl Defines the rootUrl to load from
   */
  PBRClearCoatConfiguration.prototype.parse = function (source, scene, rootUrl) {
    var _this = this;
    SerializationHelper.Parse(function () {
      return _this;
    }, source, scene, rootUrl);
  };
  /**
   * This defaults to 1.5 corresponding to a 0.04 f0 or a 4% reflectance at normal incidence
   * The default fits with a polyurethane material.
   * @hidden
   */
  PBRClearCoatConfiguration._DefaultIndexOfRefraction = 1.5;
  __decorate([serialize(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "isEnabled", void 0);
  __decorate([serialize()], PBRClearCoatConfiguration.prototype, "intensity", void 0);
  __decorate([serialize()], PBRClearCoatConfiguration.prototype, "roughness", void 0);
  __decorate([serialize(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "indexOfRefraction", void 0);
  __decorate([serializeAsTexture(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "texture", void 0);
  __decorate([serialize(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "useRoughnessFromMainTexture", void 0);
  __decorate([serializeAsTexture(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "textureRoughness", void 0);
  __decorate([serialize(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "remapF0OnInterfaceChange", void 0);
  __decorate([serializeAsTexture(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "bumpTexture", void 0);
  __decorate([serialize(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "isTintEnabled", void 0);
  __decorate([serializeAsColor3()], PBRClearCoatConfiguration.prototype, "tintColor", void 0);
  __decorate([serialize()], PBRClearCoatConfiguration.prototype, "tintColorAtDistance", void 0);
  __decorate([serialize()], PBRClearCoatConfiguration.prototype, "tintThickness", void 0);
  __decorate([serializeAsTexture(), expandToProperty("_markAllSubMeshesAsTexturesDirty")], PBRClearCoatConfiguration.prototype, "tintTexture", void 0);
  return PBRClearCoatConfiguration;
}();
export { PBRClearCoatConfiguration };